import { Link } from 'react-router-dom';
import { Chip } from '../../components/Chip';
import { QrImage } from '../../components/QrImage';
import { QrDownloadActions } from '../qrs/QrDownloadActions';
import { formatDate, KIND_LABEL } from '../qrs/states';
import { operatorQrImagePath, shortLink } from '../qrs/utils';
import { QR_TYPE_LABEL, QR_TYPE_TONE, stateChipFor, type OperatorQrDetail, type OperatorQrHistoryEntry } from './types';

function actorText(actor: OperatorQrHistoryEntry['actor']): string {
  if (!actor) return '—';
  if (typeof actor === 'string') return actor;
  return actor.name ?? actor.email ?? actor.id ?? '—';
}

function historyText(h: OperatorQrHistoryEntry): string {
  if (h.action === 'activate') return `Activado → ${h.after ?? '—'}`;
  if (h.before || h.after) return `${h.before ?? '—'} → ${h.after ?? '—'}`;
  return h.action ?? 'Cambio';
}

/** Ficha del QR para el operador (R14.2): QR grande, respaldo del QR Art, descargas, datos e historial. */
export function QrOperatorDetail({ qr }: { qr: OperatorQrDetail }) {
  const isArt = qr.qrType === 'art';
  const state = stateChipFor(qr.state);
  const origin = [qr.kind ? KIND_LABEL[qr.kind] ?? qr.kind : null, qr.lotId ? `lote ${qr.lotId}` : null].filter(Boolean).join(' · ') || '—';
  const history = [...(qr.history ?? [])].reverse();

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-mono text-xl font-semibold tracking-wider text-ink">{qr.code}</h2>
          <p className="text-sm text-ink-muted">{shortLink(qr)}</p>
        </div>
        <div className="flex flex-wrap justify-end gap-1.5">
          <Chip tone={QR_TYPE_TONE[qr.qrType]} dot={false}>{QR_TYPE_LABEL[qr.qrType]}</Chip>
          <Chip tone={state.tone}>{state.label}</Chip>
        </div>
      </div>

      {qr.hasFallback ? (
        <div className="grid grid-cols-[1.4fr,1fr] items-end gap-3">
          <figure className="space-y-1">
            <QrImage path={operatorQrImagePath(qr.id, { size: 1024 })} alt={`QR Art de ${qr.code}`} />
            <figcaption className="flex items-center gap-2 text-xs text-ink-muted"><Chip tone="error">No escanea</Chip> Arte generado</figcaption>
          </figure>
          <figure className="space-y-1">
            <QrImage path={operatorQrImagePath(qr.id, { variant: 'fallback', size: 512 })} alt={`QR de respaldo entregado para ${qr.code}`} />
            <figcaption className="flex items-center gap-2 text-xs text-ink-muted"><Chip tone="success">Entregado</Chip> QR de respaldo</figcaption>
          </figure>
        </div>
      ) : (
        <figure className="space-y-1">
          <QrImage path={operatorQrImagePath(qr.id, { size: 1024 })} alt={`QR de ${qr.code}`} />
          <figcaption className="text-xs text-ink-muted">
            {isArt
              ? 'Arte generado'
              : qr.qrType === 'styled'
                ? `Estilo ${qr.stylePreset ?? 'personalizado'} · dibujado desde el estilo guardado`
                : 'Clásico · dibujado al vuelo'}
          </figcaption>
        </figure>
      )}

      <QrDownloadActions
        code={qr.code}
        link={shortLink(qr)}
        targetUrl={qr.targetUrl}
        isArt={isArt}
        imagePath={(params) => operatorQrImagePath(qr.id, params)}
        idPrefix={`op-${qr.id}`}
      />

      {isArt && qr.prompt && (
        <p className="rounded-md bg-mist px-3 py-2 text-xs text-ink-muted">
          Prompt: “{qr.prompt}”{qr.artEngine ? ` · motor ${qr.artEngine}` : ''}
          {qr.artAttempts ? ` · ${qr.artAttempts} intento(s)` : ''}
          {typeof qr.artGenSeconds === 'number' ? ` · ${qr.artGenSeconds.toLocaleString('es-CL')} s de GPU` : ''}
        </p>
      )}

      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm">
        <dt className="text-ink-muted">Organización</dt>
        <dd>{qr.orgName ?? qr.orgId ?? <span className="text-ink-muted">Sin asignar</span>}</dd>
        <dt className="text-ink-muted">Origen</dt>
        <dd>{origin}</dd>
        <dt className="text-ink-muted">Negocio</dt>
        <dd>{qr.businessName ?? '—'}</dd>
        <dt className="text-ink-muted">Referencia</dt>
        <dd className="font-mono">{qr.externalRef ?? '—'}</dd>
        <dt className="text-ink-muted">Destino</dt>
        <dd className="break-all">{qr.targetUrl ?? <span className="text-ink-muted">Sin activar: muestra la página del partner</span>}</dd>
        <dt className="text-ink-muted">Creado</dt>
        <dd>{formatDate(qr.createdAt)}</dd>
        <dt className="text-ink-muted">Activado por</dt>
        <dd className="break-all">{qr.activatedBy ?? '—'}</dd>
      </dl>

      <div className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Historial de destinos</h3>
        {history.length ? (
          <ul className="space-y-1.5 text-sm">
            {history.map((h, i) => (
              <li key={`${h.at}-${i}`} className="grid grid-cols-[6.5rem,1fr] gap-2">
                <time className="tabular-nums text-ink-muted">{formatDate(h.at)}</time>
                <span className="break-all">{historyText(h)} <span className="text-ink-muted">· {actorText(h.actor)}</span></span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-ink-muted">Sin cambios de destino.</p>
        )}
      </div>

      <Link to={`/operador/qr/${encodeURIComponent(qr.code)}/auditoria`} className="text-sm text-green underline">
        Ver la auditoría completa del código
      </Link>

      <p className="rounded-md bg-mist px-3 py-2 text-xs text-ink-muted">Cada descarga del operador queda en auditoría.</p>
    </div>
  );
}
